<template>
  <component
    :is="hotspot.href ? 'a' : 'button'"
    v-for="hotspot in hotspots"
    :key="hotspot.label"
    class="hotspot"
    :href="hotspot.href"
    :type="hotspot.href ? undefined : 'button'"
    :target="hotspot.external ? '_blank' : undefined"
    :rel="hotspot.external ? 'noopener noreferrer' : undefined"
    :aria-label="hotspot.label"
    :style="hotspotStyle(hotspot)"
    @click="handleHotspotClick(hotspot, $event)"
  >
    <img
      v-if="hotspot.qrSrc"
      class="hotspot__qr"
      :src="hotspot.qrSrc"
      :alt="hotspot.qrAlt || ''"
      loading="lazy"
      decoding="async"
    />
  </component>

  <Transition name="email-toast">
    <div v-if="mailToast" class="email-toast" role="status" aria-live="polite">
      {{ mailToast }}
    </div>
  </Transition>
</template>

<script setup lang="ts">
import type { CSSProperties } from 'vue'

import { useMailto } from '../../composables/useMailto'
import type { Hotspot } from '../../data/home2026'

const props = defineProps<{
  hotspots: Hotspot[]
  scaledPx: (value: number) => string
  stageScale: number
}>()

const { mailToast, openMailto } = useMailto()

const clearCurrentHash = () => {
  if (!window.location.hash) return

  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
}

const hotspotStyle = (hotspot: Hotspot): CSSProperties =>
  ({
    left: props.scaledPx(hotspot.x),
    top: props.scaledPx(hotspot.y),
    width: props.scaledPx(hotspot.w),
    height: props.scaledPx(hotspot.h),
    ...(hotspot.qrSrc
      ? {
          '--hotspot-qr-size': props.scaledPx(hotspot.qrSize ?? 160),
          '--hotspot-qr-offset': props.scaledPx(hotspot.qrOffset ?? 12),
        }
      : {}),
  }) as CSSProperties

const handleHotspotClick = (hotspot: Hotspot, event: MouseEvent) => {
  if (hotspot.href?.startsWith('mailto:')) {
    event.preventDefault()
    void openMailto(hotspot.href)
    return
  }

  if (hotspot.scrollTarget) {
    event.preventDefault()
    const target = document.getElementById(hotspot.scrollTarget)
    if (!target) return

    const scrollOptions: ScrollToOptions = {
      top: Math.round(target.getBoundingClientRect().top + window.scrollY),
      behavior: 'smooth',
    }

    window.scrollTo(scrollOptions)
    document.documentElement.scrollTo(scrollOptions)
    document.body.scrollTo(scrollOptions)
    clearCurrentHash()

    return
  }

  if (hotspot.scrollTo === undefined) return

  event.preventDefault()
  const scrollOptions: ScrollToOptions = {
    top: Math.round(hotspot.scrollTo * props.stageScale),
    behavior: 'smooth',
  }

  window.scrollTo(scrollOptions)
  document.documentElement.scrollTo(scrollOptions)
  document.body.scrollTo(scrollOptions)
  clearCurrentHash()
}
</script>

<style scoped>
.hotspot {
  position: absolute;
  z-index: 5;
  display: block;
  padding: 0;
  border: 0;
  appearance: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
}

.hotspot:focus-visible {
  outline: var(--focus-outline-width) solid rgb(138 188 249 / 90%);
  outline-offset: var(--focus-outline-offset);
}

.hotspot__qr {
  position: absolute;
  top: auto;
  bottom: calc(100% + var(--hotspot-qr-offset, 12px));
  left: 50%;
  z-index: 6;
  display: block;
  width: var(--hotspot-qr-size, 160px);
  height: var(--hotspot-qr-size, 160px);
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 14px 34px rgb(0 0 0 / 35%);
  opacity: 0;
  pointer-events: none;
  transform: translate(-50%, 6px);
  transition:
    opacity 160ms ease,
    transform 160ms ease;
}

.hotspot:hover .hotspot__qr,
.hotspot:focus-visible .hotspot__qr {
  opacity: 1;
  pointer-events: auto;
  transform: translate(-50%, 0);
}

.email-toast {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 50;
  max-width: min(360px, calc(100vw - 48px));
  padding: 12px 16px;
  border: 1px solid rgb(255 255 255 / 18%);
  border-radius: 999px;
  background: rgb(17 17 17 / 92%);
  box-shadow: 0 14px 34px rgb(0 0 0 / 35%);
  color: #fff;
  font-family: AlibabaPuHuiTi_3_65_Medium, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  pointer-events: none;
}

.email-toast-enter-active,
.email-toast-leave-active {
  transition:
    opacity 160ms ease,
    transform 160ms ease;
}

.email-toast-enter-from,
.email-toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (prefers-reduced-motion: reduce) {
  .hotspot {
    scroll-behavior: auto;
  }

  .hotspot__qr {
    transition: none;
  }

  .email-toast-enter-active,
  .email-toast-leave-active {
    transition: none;
  }
}
</style>
